/**
 * Cliente de los endpoints de autenticación del backoffice.
 * El contrato exacto está documentado en `../types.ts`.
 */
import { apiRequest } from '@/lib/http/apiClient';
import type {
  AuthTokensResponse,
  AuthUser,
  EnrollCompleteInput,
  EnrollSetupResponse,
  LoginResponse,
} from '../types';

export const authApi = {
  /** Primer factor: email + password. */
  login(email: string, password: string): Promise<LoginResponse> {
    return apiRequest<LoginResponse>('/api/v1/admin/auth/login', {
      method: 'POST',
      body: { email, password },
      auth: false,
      retryOnUnauthorized: false,
    });
  },

  /** Segundo factor: código TOTP de 6 dígitos + el `userId` devuelto por el login. */
  verifyTotp(userId: string, code: string): Promise<AuthTokensResponse> {
    return apiRequest<AuthTokensResponse>('/api/v1/admin/auth/2fa/verify', {
      method: 'POST',
      body: { userId, code },
      auth: false,
      retryOnUnauthorized: false,
    });
  },

  /** Identidad autenticada (requiere Bearer). */
  me(): Promise<AuthUser> {
    return apiRequest<AuthUser>('/api/v1/admin/auth/me');
  },

  /** Rota la cookie httpOnly de refresh y emite un nuevo access token. */
  refresh(): Promise<AuthTokensResponse> {
    return apiRequest<AuthTokensResponse>('/api/v1/admin/auth/refresh', {
      method: 'POST',
      auth: false,
      retryOnUnauthorized: false,
    });
  },

  /** Revoca la sesión y limpia la cookie. */
  logout(): Promise<void> {
    return apiRequest<void>('/api/v1/admin/auth/logout', { method: 'POST', retryOnUnauthorized: false });
  },

  /** Primer acceso, paso 1: valida la invitación y devuelve el QR TOTP. */
  enrollSetup(email: string, enrollmentToken: string): Promise<EnrollSetupResponse> {
    return apiRequest<EnrollSetupResponse>('/api/v1/admin/auth/enroll/setup', {
      method: 'POST',
      body: { email, enrollmentToken },
      auth: false,
      retryOnUnauthorized: false,
    });
  },

  /** Primer acceso, paso 2: define password + primer código TOTP y abre sesión. */
  enrollComplete(input: EnrollCompleteInput): Promise<AuthTokensResponse> {
    return apiRequest<AuthTokensResponse>('/api/v1/admin/auth/enroll/complete', {
      method: 'POST',
      body: input,
      auth: false,
      retryOnUnauthorized: false,
    });
  },
};
